import { Controller } from "@hotwired/stimulus"

// Drives the two-axis pricing toggle: currency (USD / CAD) × period (monthly / annual).
// Amount elements carry four data attributes covering all combinations:
//   data-monthly-usd, data-monthly-cad, data-annual-usd, data-annual-cad
// The controller reads the correct one based on current state and writes it to
// the element's textContent.
//
// Period-label targets update between "/ month" and "/ mo, billed annually".
// Save-note targets are shown only when annual is active.
export default class extends Controller {
  static targets = [
    "track", "button",          // currency toggle
    "amount", "currency",       // amount + "USD"/"CAD" label
    "periodTrack", "periodButton", // period toggle
    "periodLabel",              // "/ month" vs "/ mo, billed annually"
    "saveNote",                 // "Save X — 2 months free" (hidden on monthly)
  ]

  #currency = "USD"
  #period = "monthly"

  connect() {
    this.#apply()
  }

  // Called by currency buttons
  set(event) {
    const code = event.currentTarget.dataset.currency
    if (code !== "USD" && code !== "CAD") return
    this.#currency = code
    this.#apply()
  }

  // Called by period buttons
  setPeriod(event) {
    const period = event.currentTarget.dataset.period
    if (period !== "monthly" && period !== "annual") return
    this.#period = period
    this.#apply()
  }

  #apply() {
    const currency = this.#currency
    const period = this.#period

    // dataset key: "monthlyUsd", "monthlyCad", "annualUsd", "annualCad"
    const key = period + currency.charAt(0).toUpperCase() + currency.slice(1).toLowerCase()

    // --- currency track & buttons ---
    if (this.hasTrackTarget) this.trackTarget.dataset.currency = currency

    this.amountTargets.forEach((el) => {
      el.textContent = el.dataset[key] ?? el.dataset[currency.toLowerCase()]
    })

    this.currencyTargets.forEach((el) => {
      el.textContent = currency
    })

    this.buttonTargets.forEach((btn) => {
      const active = btn.dataset.currency === currency
      btn.setAttribute("aria-pressed", active ? "true" : "false")
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("text-ink-muted", !active)
    })

    // --- period track & buttons ---
    if (this.hasPeriodTrackTarget) this.periodTrackTarget.dataset.period = period

    this.periodButtonTargets.forEach((btn) => {
      const active = btn.dataset.period === period
      btn.setAttribute("aria-pressed", active ? "true" : "false")
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("text-ink-muted", !active)
    })

    // --- period-dependent labels ---
    this.periodLabelTargets.forEach((el) => {
      el.textContent = period === "annual" ? "/ mo, billed annually" : "/ month"
    })

    // --- save notes: visible only on annual ---
    this.saveNoteTargets.forEach((el) => {
      el.classList.toggle("hidden", period !== "annual")
    })
  }
}
